import React from 'react';
import styled from 'styled-components';
import { FaEnvelope, FaMapMarkerAlt, FaBriefcase, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { contactInfo } from '../data/personal';
import { useContactForm } from '../hooks';
import { Section, Card, Button } from '../components/common';
import { FormInput, FormTextarea } from '../components/forms';

const ContactContainer = styled(Section)`
  background: ${({ theme }: { theme: any }) => theme.colors.background};
`;

const ContactContent = styled.div`
  .section-title {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes['4xl']};
    font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.bold};
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.lg};
    background: linear-gradient(135deg, ${({ theme }: { theme: any }) => theme.colors.navyBlue}, ${({ theme }: { theme: any }) => theme.colors.lightNavy});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: center;

    @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }: { theme: any }) => theme.fontSizes['3xl']};
    }
  }

  .section-subtitle {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
    color: ${({ theme }: { theme: any }) => theme.colors.textSecondary};
    line-height: 1.7;
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing['2xl']};
    text-align: center;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${({ theme }: { theme: any }) => theme.spacing['3xl']};
  margin-top: ${({ theme }: { theme: any }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }: { theme: any }) => theme.spacing['2xl']};
  }
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    gap: ${({ theme }: { theme: any }) => theme.spacing.xl};
    margin-top: ${({ theme }: { theme: any }) => theme.spacing.xl};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }: { theme: any }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.lg}) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${({ theme }: { theme: any }) => theme.spacing.md};
  }
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }: { theme: any }) => theme.spacing.md};
  }
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${({ theme }: { theme: any }) => theme.spacing.md};
  padding: ${({ theme }: { theme: any }) => theme.spacing.xl};
  transition: all ${({ theme }: { theme: any }) => theme.transitions.normal};
  
  /* Better touch targets */
  min-height: 80px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(30, 58, 138, 0.2);
    border-color: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      transform: none; /* Disable hover transform on touch devices */
      box-shadow: none;
    }
  }
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    padding: ${({ theme }: { theme: any }) => theme.spacing.lg};
    min-height: 70px;
  }

  .info-icon {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.xl};
    color: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
    flex-shrink: 0;
  }

  .info-content {
    .info-label {
      font-size: ${({ theme }: { theme: any }) => theme.fontSizes.sm};
      color: ${({ theme }: { theme: any }) => theme.colors.textMuted};
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.xs};
      
      @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
        font-size: ${({ theme }: { theme: any }) => theme.fontSizes.xs};
        letter-spacing: 0.5px;
      }
    }

    .info-value {
      font-size: ${({ theme }: { theme: any }) => theme.fontSizes.base};
      font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.medium};
      color: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
      
      @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
        font-size: ${({ theme }: { theme: any }) => theme.fontSizes.sm};
      }
    }
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }: { theme: any }) => theme.spacing.lg};
  background: ${({ theme }: { theme: any }) => theme.colors.surface};
  padding: ${({ theme }: { theme: any }) => theme.spacing['2xl']};
  border-radius: ${({ theme }: { theme: any }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }: { theme: any }) => theme.colors.border};
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    padding: ${({ theme }: { theme: any }) => theme.spacing.xl};
    gap: ${({ theme }: { theme: any }) => theme.spacing.md};
  }
`;

const FormTitle = styled.h3`
  font-size: ${({ theme }: { theme: any }) => theme.fontSizes.xl};
  font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.semibold};
  color: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.md};
  text-align: center;
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.sm};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }: { theme: any }) => theme.spacing.md};

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }: { theme: any }) => theme.spacing.sm};
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }: { theme: any }) => theme.spacing.sm};
  padding: ${({ theme }: { theme: any }) => theme.spacing.md};
  border-radius: ${({ theme }: { theme: any }) => theme.borderRadius.md};
  font-size: ${({ theme }: { theme: any }) => theme.fontSizes.sm};
  font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.medium};

  background: ${({ type }) => 
    type === 'success' 
      ? 'rgba(34, 197, 94, 0.1)' 
      : 'rgba(239, 68, 68, 0.1)'};
  
  color: ${({ type }) => 
    type === 'success' 
      ? '#22c55e' 
      : '#ef4444'};
  
  border: 1px solid ${({ type }) => 
    type === 'success' 
      ? 'rgba(34, 197, 94, 0.2)' 
      : 'rgba(239, 68, 68, 0.2)'};
  
  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.sm}) {
    padding: ${({ theme }: { theme: any }) => theme.spacing.sm};
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.xs};
  }

  .status-icon {
    flex-shrink: 0;
  }
`;

export const ContactSection: React.FC = () => {
  const { formData, loading, success, error, handleChange, handleSubmit, resetForm } = useContactForm();

  return (
    <ContactContainer id="contact">
        <ContactContent>
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to discuss your next project or explore new opportunities? 
            I'd love to hear from you. Let's build something amazing together.
          </p>

          <ContactGrid>
            <ContactInfo>
              <InfoCard hover>
                {React.createElement(FaEnvelope as any, { className: "info-icon" })}
                <div className="info-content">
                  <div className="info-label">Email</div>
                  <div className="info-value">{contactInfo.email}</div>
                </div>
              </InfoCard>

              <InfoCard hover>
                {React.createElement(FaMapMarkerAlt as any, { className: "info-icon" })}
                <div className="info-content">
                  <div className="info-label">Location</div>
                  <div className="info-value">{contactInfo.location}</div>
                </div>
              </InfoCard>

              <InfoCard hover>
                {React.createElement(FaBriefcase as any, { className: "info-icon" })}
                <div className="info-content">
                  <div className="info-label">Current Role</div>
                  <div className="info-value">{contactInfo.role}</div>
                </div>
              </InfoCard>

              <InfoCard hover>
                {React.createElement(FaCheckCircle as any, { className: "info-icon" })}
                <div className="info-content">
                  <div className="info-label">Availability</div>
                  <div className="info-value">{contactInfo.availability}</div>
                </div>
              </InfoCard>
            </ContactInfo>

            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>Send me a message</FormTitle>
              
              {success && (
                <StatusMessage type="success">
                  {React.createElement(FaCheckCircle as any, { className: "status-icon" })}
                  Thank you for your message! I'll get back to you soon.
                </StatusMessage>
              )}

              {error && (
                <StatusMessage type="error">
                  {React.createElement(FaExclamationCircle as any, { className: "status-icon" })}
                  {error}
                </StatusMessage>
              )}

              <FormRow>
                <FormInput
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  label="Name"
                  required
                  disabled={loading}
                />
                <FormInput
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  label="Email"
                  required
                  disabled={loading}
                />
              </FormRow>

              <FormInput
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What's this about?"
                label="Subject"
                required
                disabled={loading}
              />

              <FormTextarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your project, ideas, or just say hello..."
                label="Message"
                required
                disabled={loading}
                rows={6}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                disabled={loading || !formData.name || !formData.email || !formData.subject || !formData.message}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>

              {success && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={resetForm}
                >
                  Send Another Message
                </Button>
              )}
            </ContactForm>
          </ContactGrid>
        </ContactContent>
             </ContactContainer>
  );
}; 